import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { getTime } from '@utils/unacademyIcons/playerHelper';
import Paper from '@unicons-base/Paper/Paper';
import { Fonts, mediaQuery } from '@styles/index';
import Typography from '@unicons-base/Typography/Typography';
import {
  SPACING_2B,
  SPACING_3B,
  SPACING_6,
  SPACING_5B,
  SPACING_6B,
  SPACING_8B
} from '@styles/constants/spacings';
import UN_ICONS_TYPOGRAPHY_VARIANT from 'types/unacademyIcons/typography.type';
import { useLessonPlayer } from '@stores/lessonPlayerStore';
import LessonThumbnail from './LessonThumbnail';

interface LessonCardProps {
  order: number;
  duration: number;
  cover: string;
  slug: string;
  title: string;
  description: string;
  continueWatching: boolean | null;
  hasActiveSubscription: boolean;
  isCompactCard?: boolean;
  isComingSoon: boolean;
  bgColor?: string;
  publishedAt?: string | null;
}

type DescriptionType = {
  $isMobileDescription: boolean;
};

type CompactType = { $isCompactCard: boolean; $isLessonActive?: boolean };

const MainContentWrapper = styled.div`
  display: flex;
`;

const StyledHeading = styled.div<CompactType>`
  margin-left: ${({ $isCompactCard }) =>
    $isCompactCard ? SPACING_3B : SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-left: ${SPACING_3B};
  }
`;

const StyledTitle = styled(Typography)<CompactType>`
  color: var(--color-i-divider);
  font-size: ${({ $isCompactCard }) => ($isCompactCard ? Fonts.H6 : '18px')};
  font-weight: ${Fonts.BOLD};
  background: ${({ $isLessonActive }) =>
    $isLessonActive &&
    'linear-gradient(261.69deg, var(--color-i-icon-gradient-1) 0%, var(--color-i-icon-gradient-2) 69.95%, var(--color-i-icon-gradient-3) 100%)'};
  -webkit-background-clip: ${({ $isLessonActive }) =>
    $isLessonActive && 'text'};
  background-clip: ${({ $isLessonActive }) => $isLessonActive && 'text'};
  -webkit-text-fill-color: ${({ $isLessonActive }) =>
    $isLessonActive && 'transparent'};
  margin-bottom: ${({ $isCompactCard }) =>
    $isCompactCard ? SPACING_2B : SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P1};
    font-weight: ${Fonts.SEMIBOLD};
    margin-bottom: ${SPACING_6};
  }
`;

const StyledSubTitle = styled(Typography)<CompactType>`
  color: var(--color-i-icon-dark-secondary);
  font-size: ${({ $isCompactCard }) =>
    $isCompactCard ? Fonts.H7 : Fonts.SMALL};
  font-weight: ${({ $isCompactCard }) =>
    $isCompactCard ? Fonts.REGULAR : Fonts.SEMIBOLD};
  margin-bottom: 0;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
    font-weight: ${Fonts.REGULAR};
  }
`;

const StyledDescription = styled(Typography)<DescriptionType>`
  color: var(--color-i-icon-dark-secondary);
  font-size: ${Fonts.SMALL};
  font-weight: ${Fonts.REGULAR};
  margin-top: ${SPACING_6B};
  line-height: 22px;
  display: ${({ $isMobileDescription }) =>
    $isMobileDescription ? 'none' : 'inline-block'};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
    font-weight: ${Fonts.REGULAR};
    line-height: 1.5;
    margin-top: ${SPACING_3B};
    display: ${({ $isMobileDescription }) =>
      $isMobileDescription ? 'inline-block' : 'none'};
  }
`;

const StyledPaper = styled(Paper)<CompactType>`
  display: flex;
  flex-direction: column;
  padding: ${({ $isCompactCard }) =>
    $isCompactCard ? `0px 0px ${SPACING_5B}` : `0px 0px ${SPACING_8B}`};
`;

const LessonCard: React.FC<LessonCardProps> = ({
  order,
  duration,
  cover,
  slug,
  title,
  description,
  continueWatching,
  hasActiveSubscription,
  isCompactCard,
  isComingSoon,
  bgColor,
  publishedAt
}) => {
  const lessonSlug = useLessonPlayer.use.lessonSlug();
  const isLessonActive = lessonSlug === slug && isCompactCard;
  const minutes = getTime(duration, true);
  const router = useRouter();

  const courseSlug = useMemo(() => {
    return router.asPath.split('/')[3];
  }, [router]);

  const courseSubtitle = useMemo(() => {
    if (duration && duration > 0) return `Lesson ${order} • ${minutes} min`;
    return `Lesson ${order}`;
  }, [order, duration, minutes]);

  return (
    <StyledPaper bgColor={bgColor} $isCompactCard={isCompactCard}>
      <MainContentWrapper>
        <LessonThumbnail
          cover={cover}
          continueWatching={continueWatching}
          duration={duration}
          slug={slug}
          courseSlug={courseSlug}
          isSubscribed={hasActiveSubscription}
          isCompactCard={isCompactCard}
          isLessonActive={isLessonActive}
          isComingSoon={isComingSoon}
          publishedAt={publishedAt}
        />
        <StyledHeading $isCompactCard={isCompactCard}>
          <StyledTitle
            variant={UN_ICONS_TYPOGRAPHY_VARIANT.H2}
            $isCompactCard={isCompactCard}
            $isLessonActive={isLessonActive}
          >
            {title}
          </StyledTitle>
          <StyledSubTitle
            variant={UN_ICONS_TYPOGRAPHY_VARIANT.BODY1}
            $isCompactCard={isCompactCard}
          >
            {courseSubtitle}
          </StyledSubTitle>
          {description && !isCompactCard && (
            <StyledDescription $isMobileDescription={false}>
              {description}
            </StyledDescription>
          )}
        </StyledHeading>
      </MainContentWrapper>
      {description && !isCompactCard && (
        <StyledDescription $isMobileDescription>
          {description}
        </StyledDescription>
      )}
    </StyledPaper>
  );
};

LessonCard.defaultProps = {
  isCompactCard: false,
  bgColor: 'var(--color-i-all-black)',
  publishedAt: null
};

LessonCard.displayName = 'LessonCard';

export default LessonCard;
